import React, { useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { Box, Typography, TextField, LinearProgress, InputAdornment, Skeleton, Tooltip } from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch, getAuthHeaders } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';

/* ── Stitch tokens ─────────────────────────────────────────────────────────── */
const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  bg: '#f8f9fa', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5', error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
  shadowHover: '0 8px 24px rgba(45,51,53,0.08)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const COMPRESSION = [
  { value: 'original', label: 'Origineel', desc: 'Geen compressie — groot bestand, beste kwaliteit voor print', icon: 'high_quality' },
  { value: 'standard', label: 'Standaard', desc: 'Goede balans tussen kwaliteit en bestandsgrootte', icon: 'tune' },
  { value: 'smallest', label: 'Kleinst', desc: 'Maximale compressie — afbeeldingen kunnen pixelig worden, alleen gebruiken als bestandsgrootte echt een probleem is', icon: 'compress', warn: true },
];

const PROCESS_STEPS = [
  { key: 'prepare', label: 'Presentatie voorbereiden', icon: 'settings', duration: 3000 },
  { key: 'convert', label: 'Omzetten naar PDF', icon: 'swap_horiz', duration: 8000 },
  { key: 'links', label: 'Hyperlinks toevoegen', icon: 'link', duration: 4000 },
  { key: 'save', label: 'PDF opslaan', icon: 'save', duration: 2000 },
];

/* ── Badge component ───────────────────────────────────────────────────────── */
const Badge = ({ label, color = T.primary, bg = T.primaryContainer }) => (
  <Typography component="span" sx={{
    fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700,
    color, bgcolor: bg, px: 1.25, py: 0.3, borderRadius: '9999px',
    display: 'inline-block', textTransform: 'uppercase', letterSpacing: '0.06em',
  }}>
    {label}
  </Typography>
);

/* ── Icon badge ────────────────────────────────────────────────────────────── */
const IconBadge = ({ name, size = 48, iconSize = 26, bg = T.primaryContainer, color = T.primary }) => (
  <Box sx={{
    width: size, height: size, borderRadius: '0.75rem', bgcolor: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }}>
    <Icon name={name} size={iconSize} sx={{ color }} />
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function PPTXVideoReplacer() {
  const [sessionId, setSessionId] = useState(null);
  const [originalName, setOriginalName] = useState('');
  const [videos, setVideos] = useState([]);
  const [slideCount, setSlideCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processStep, setProcessStep] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [outputFilename, setOutputFilename] = useState('');
  const fileInputRef = useRef(null);
  const stepTimerRef = useRef(null);
  const [compression, setCompression] = useState('standard');
  const [videoSettings, setVideoSettings] = useState({});
  const [slideChainIcon, setSlideChainIcon] = useState({});
  const [dragOver, setDragOver] = useState(false);

  const updateSetting = (index, key, value) => {
    setVideoSettings(prev => ({ ...prev, [index]: { ...prev[index], [key]: value } }));
  };

  /* ── Upload ────────────────────────────────────────────────────────────── */
  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadPhase('uploading');
    setDownloadUrl(null);
    setSessionId(null);
    setVideos([]);
    setVideoSettings({});

    try {
      const authHeaders = await getAuthHeaders();
      const formData = new FormData();
      formData.append('pptx', file);

      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/Api/pptx-video/upload`);
        Object.entries(authHeaders).forEach(([k, v]) => xhr.setRequestHeader(k, v));

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.upload.onload = () => { setUploadPhase('analyzing'); setUploadProgress(100); };
        xhr.onload = () => {
          try {
            const parsed = JSON.parse(xhr.responseText);
            if (xhr.status >= 400) reject(new Error(parsed.error || 'Upload mislukt'));
            else resolve(parsed);
          } catch { reject(new Error(`Server error (${xhr.status})`)); }
        };
        xhr.onerror = () => reject(new Error('Verbinding mislukt'));
        xhr.send(formData);
      });

      setSessionId(data.sessionId);
      setOriginalName(data.originalName);
      setVideos(data.videos);
      setSlideCount(data.slideCount);

      const initial = {};
      const initialSlideIcons = {};
      let autoFilledCount = 0;
      data.videos.forEach(v => {
        let autoUrl = '';
        if (v.description) {
          const urlMatch = v.description.match(/https?:\/\/[^\s"<>]+/i);
          if (urlMatch) { autoUrl = urlMatch[0]; autoFilledCount++; }
        }
        initial[v.index] = { url: autoUrl };
        initialSlideIcons[v.slideNum] = true;
      });
      setVideoSettings(initial);
      setSlideChainIcon(initialSlideIcons);

      if (data.videos.length === 0) {
        toast('Geen video\'s gevonden in deze presentatie', { icon: '⚠️' });
      } else {
        const slideSet = new Set(data.videos.map(v => v.slideNum));
        let msg = `${data.videos.length} video${data.videos.length > 1 ? '\'s' : ''} gevonden op ${slideSet.size} slide${slideSet.size > 1 ? 's' : ''}`;
        if (autoFilledCount > 0) msg += ` — ${autoFilledCount} URL${autoFilledCount > 1 ? "'s" : ''} automatisch ingevuld`;
        toast.success(msg);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadPhase('');
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.name.endsWith('.pptx')) handleUpload(file);
    else toast.error('Alleen .pptx bestanden');
  }, [handleUpload]);

  /* ── Process ───────────────────────────────────────────────────────────── */
  const handleProcess = async () => {
    const settings = Object.entries(videoSettings)
      .filter(([_, s]) => s.url?.trim())
      .map(([index, s]) => {
        const video = videos.find(v => v.index === parseInt(index));
        return {
          index: parseInt(index),
          url: s.url.trim(),
          addChainIcon: video ? (slideChainIcon[video.slideNum] ?? true) : false,
        };
      });

    if (settings.length === 0) { toast.error('Vul minimaal 1 URL in'); return; }

    setProcessing(true);
    setProcessStep(0);

    let step = 0;
    const advanceStep = () => {
      step++;
      if (step < PROCESS_STEPS.length) {
        setProcessStep(step);
        stepTimerRef.current = setTimeout(advanceStep, PROCESS_STEPS[step].duration);
      }
    };
    stepTimerRef.current = setTimeout(advanceStep, PROCESS_STEPS[0].duration);

    try {
      const res = await authFetch(`${BACKEND_URL}/Api/pptx-video/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, videoSettings: settings, compression }),
      });

      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Verwerking mislukt'); }

      const data = await res.json();
      clearTimeout(stepTimerRef.current);
      setProcessStep(PROCESS_STEPS.length);
      setProcessing(false);
      setDownloadUrl(`${BACKEND_URL}${data.downloadUrl}`);
      setOutputFilename(data.filename);
      toast.success('PDF gegenereerd!');
    } catch (err) {
      clearTimeout(stepTimerRef.current);
      toast.error(err.message);
      setProcessing(false);
      setProcessStep(0);
    }
  };

  const handleReset = () => {
    setSessionId(null);
    setOriginalName('');
    setVideos([]);
    setVideoSettings({});
    setSlideChainIcon({});
    setSlideCount(0);
    setDownloadUrl(null);
    setOutputFilename('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* ── Derived ───────────────────────────────────────────────────────────── */
  const videosBySlide = {};
  videos.forEach(v => {
    if (!videosBySlide[v.slideNum]) videosBySlide[v.slideNum] = [];
    videosBySlide[v.slideNum].push(v);
  });
  const filledCount = Object.values(videoSettings).filter(s => s.url?.trim()).length;

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

      <PageHeader
        icon="slideshow"
        title="Video Replacer"
        subtitle="PowerPoint met video's omzetten naar PDF met klikbare links"
        accent={{ color: T.tertiary, bg: T.tertiaryContainer }}
      />

      {/* ── Tip ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        p: 2, mb: 4, mt: 2, borderRadius: '0.75rem',
        bgcolor: T.amberBg, border: `1px solid ${T.amber}25`,
      }}>
        <Icon name="lightbulb" size={18} sx={{ color: T.amber, marginTop: 1 }} />
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: '#92400e', lineHeight: 1.6 }}>
          Voeg in PowerPoint alvast de URL toe via het alt-tekstveld van de video
          (rechtermuisklik → <em>View Alt Text...</em>). De URL wordt dan automatisch ingevuld.
        </Typography>
      </Box>

      {/* ── Upload zone ─────────────────────────────────────────────────── */}
      {!sessionId && !processing && !downloadUrl && (
        <Box
          onDrop={handleDrop}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => !uploading && fileInputRef.current?.click()}
          sx={{
            p: { xs: 5, md: 7 }, textAlign: 'center',
            borderRadius: '1rem',
            border: `2px dashed ${dragOver ? T.tertiary : T.outlineVariant}`,
            bgcolor: dragOver ? `${T.tertiaryContainer}40` : T.surface,
            cursor: uploading ? 'default' : 'pointer',
            transition: 'all 0.2s',
            boxShadow: T.shadow,
            '&:hover': uploading ? {} : {
              borderColor: T.tertiary,
              bgcolor: `${T.tertiaryContainer}30`,
            },
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pptx"
            hidden
            onChange={e => handleUpload(e.target.files[0])}
          />

          {uploading ? (
            <Box sx={{ maxWidth: 340, mx: 'auto' }}>
              {uploadPhase === 'uploading' ? (
                <>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '1rem', mx: 'auto', mb: 2.5,
                    bgcolor: T.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon name="cloud_upload" size={32} sx={{ color: T.primary }} />
                  </Box>
                  <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 700, color: T.text, mb: 1 }}>
                    Uploaden… {uploadProgress}%
                  </Typography>
                  <LinearProgress
                    variant="determinate" value={uploadProgress}
                    sx={{
                      height: 6, borderRadius: 3, bgcolor: T.surfaceLow,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${T.tertiary}, ${T.primary})`,
                      },
                    }}
                  />
                </>
              ) : (
                <>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '1rem', mx: 'auto', mb: 2.5,
                    bgcolor: T.tertiaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.6 },
                    },
                  }}>
                    <Icon name="manage_search" size={32} sx={{ color: T.tertiary }} />
                  </Box>
                  <Typography sx={{ fontFamily: T.font, fontSize: '1.1rem', fontWeight: 700, color: T.text, mb: 0.5 }}>
                    Analyseren…
                  </Typography>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary }}>
                    Video's en thumbnails worden gedetecteerd
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{
                width: 72, height: 72, borderRadius: '1rem', mx: 'auto', mb: 2.5,
                bgcolor: T.tertiaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.2s',
                ...(dragOver && { transform: 'scale(1.1)' }),
              }}>
                <Icon name="upload_file" size={36} sx={{ color: T.tertiary }} />
              </Box>
              <Typography sx={{ fontFamily: T.font, fontSize: '1.15rem', fontWeight: 700, color: T.text, mb: 0.5 }}>
                Sleep een .pptx bestand hierheen
              </Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline }}>
                of klik om te bladeren — max 2 GB
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* ── Results: video editor ───────────────────────────────────────── */}
      {sessionId && !processing && !downloadUrl && (
        <>
          {/* File header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            bgcolor: T.surface, borderRadius: '0.75rem', p: 2, mb: 3, boxShadow: T.shadow,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconBadge name="description" size={36} iconSize={20} bg={T.surfaceLow} color={T.primary} />
              <Box>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', fontWeight: 700, color: T.text }}>
                  {originalName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Badge label={`${slideCount} slides`} />
                  <Badge label={`${videos.length} video${videos.length !== 1 ? "'s" : ''}`} color={T.tertiary} bg={T.tertiaryContainer} />
                </Box>
              </Box>
            </Box>
            <Box
              onClick={handleReset}
              sx={{
                width: 36, height: 36, borderRadius: '0.5rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: T.errorBg },
              }}
            >
              <Icon name="close" size={20} sx={{ color: T.outline }} />
            </Box>
          </Box>

          {videos.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Icon name="videocam_off" size={48} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 16px' }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.92rem', color: T.outline }}>
                Geen video's gevonden in deze presentatie
              </Typography>
            </Box>
          )}

          {/* Slides */}
          {Object.entries(videosBySlide).map(([slideNum, slideVideos]) => (
            <Box key={slideNum} sx={{
              bgcolor: T.surface, borderRadius: '0.75rem', p: 0, mb: 2, boxShadow: T.shadow,
              overflow: 'hidden',
            }}>
              {/* Slide header */}
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2.5, py: 1.5, bgcolor: T.surfaceLow, borderBottom: `1px solid ${T.outlineVariant}20`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Icon name="slideshow" size={18} sx={{ color: T.primary }} />
                  <Typography sx={{ fontFamily: T.font, fontSize: '0.82rem', fontWeight: 700, color: T.text }}>
                    Slide {slideNum}
                  </Typography>
                  {slideVideos.length > 1 && (
                    <Badge label={`${slideVideos.length} video's`} color={T.tertiary} bg={T.tertiaryContainer} />
                  )}
                </Box>

                {/* Chain icon toggle */}
                <Box
                  onClick={() => setSlideChainIcon(prev => ({ ...prev, [slideNum]: !(prev[slideNum] ?? true) }))}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
                    px: 1.25, py: 0.5, borderRadius: '9999px',
                    bgcolor: (slideChainIcon[slideNum] ?? true) ? `${T.emerald}15` : T.surfaceLow,
                    border: `1px solid ${(slideChainIcon[slideNum] ?? true) ? `${T.emerald}30` : 'transparent'}`,
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: (slideChainIcon[slideNum] ?? true) ? `${T.emerald}25` : T.surfaceHigh },
                  }}
                >
                  <Icon
                    name={(slideChainIcon[slideNum] ?? true) ? 'link' : 'link_off'}
                    size={14}
                    sx={{ color: (slideChainIcon[slideNum] ?? true) ? T.emerald : T.outlineVariant }}
                  />
                  <Typography sx={{
                    fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600,
                    color: (slideChainIcon[slideNum] ?? true) ? T.emerald : T.outlineVariant,
                  }}>
                    Link-icoon
                  </Typography>
                </Box>
              </Box>

              {/* Videos in slide */}
              <Box sx={{ p: 2.5 }}>
                {slideVideos.map((video, i) => {
                  const settings = videoSettings[video.index] || {};
                  const hasUrl = !!settings.url?.trim();
                  return (
                    <Box key={video.index}>
                      {i > 0 && <Box sx={{ height: 1, bgcolor: T.surfaceLow, my: 2 }} />}
                      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                        {/* Thumbnail */}
                        <Box sx={{
                          width: 180, minWidth: 180, height: 110, borderRadius: '0.5rem',
                          overflow: 'hidden', position: 'relative', flexShrink: 0,
                          bgcolor: T.surfaceLow, border: `1px solid ${T.outlineVariant}30`,
                        }}>
                          {video.thumbnailUrl ? (
                            <Box
                              component="img"
                              src={`${BACKEND_URL}${video.thumbnailUrl}`}
                              alt={video.name}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          ) : (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Icon name="play_circle" size={40} sx={{ color: T.outlineVariant }} />
                            </Box>
                          )}
                          {/* Overlay badge */}
                          <Box sx={{
                            position: 'absolute', bottom: 6, right: 6,
                            bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '0.35rem',
                            px: 0.75, py: 0.25, display: 'flex', alignItems: 'center', gap: 0.5,
                          }}>
                            <Icon name="videocam" size={12} sx={{ color: '#fff' }} />
                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 600, color: '#fff' }}>
                              VIDEO
                            </Typography>
                          </Box>
                        </Box>

                        {/* Settings */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text, mb: 0.25 }}>
                            {video.name}
                          </Typography>
                          {video.description && (
                            <Typography sx={{
                              fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant,
                              mb: 1, fontStyle: 'italic',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              Alt: {video.description}
                            </Typography>
                          )}

                          <TextField
                            fullWidth size="small"
                            placeholder="https://..."
                            value={settings.url || ''}
                            onChange={e => updateSetting(video.index, 'url', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon name={hasUrl ? 'check_circle' : 'link'} size={16} sx={{ color: hasUrl ? T.emerald : T.outlineVariant }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '0.5rem', fontSize: '0.82rem', bgcolor: T.surfaceLow,
                                fontFamily: T.fontBody,
                                '& fieldset': { borderColor: hasUrl ? `${T.emerald}40` : 'rgba(45,51,53,0.08)' },
                                '&:hover fieldset': { borderColor: hasUrl ? `${T.emerald}60` : T.outlineVariant },
                                '&.Mui-focused fieldset': { borderColor: T.primary },
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}

          {/* ── Action bar (sticky) ──────────────────────────────────────── */}
          {videos.length > 0 && (
            <Box sx={{
              position: 'sticky', bottom: 16, zIndex: 10, mt: 3,
              bgcolor: T.surface, borderRadius: '0.75rem', p: 2,
              boxShadow: '0 -4px 24px rgba(45,51,53,0.08), 0 2px 8px rgba(45,51,53,0.06)',
              border: `1px solid ${T.outlineVariant}20`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 2,
            }}>
              {/* URL count */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '0.5rem',
                  bgcolor: filledCount === videos.length ? T.emeraldBg : T.surfaceLow,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon
                    name={filledCount === videos.length ? 'check_circle' : 'link'}
                    size={18}
                    sx={{ color: filledCount === videos.length ? T.emerald : T.outline }}
                  />
                </Box>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, whiteSpace: 'nowrap' }}>
                  {filledCount}/{videos.length} URL{videos.length !== 1 ? "'s" : ''}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Compression selector */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {COMPRESSION.map(opt => (
                    <Tooltip
                      key={opt.value}
                      title={opt.desc}
                      arrow
                      placement="top"
                      slotProps={{ tooltip: {
                        sx: {
                          fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 500,
                          bgcolor: opt.warn ? '#92400e' : T.text, color: '#fff',
                          borderRadius: '0.5rem',
                          px: 1.5, py: 0.75, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          maxWidth: 240,
                        },
                      }, arrow: { sx: { color: opt.warn ? '#92400e' : T.text, '&::before': { border: 'none' } } } }}
                    >
                      <Box
                        onClick={() => setCompression(opt.value)}
                        sx={{
                          px: 1.25, py: 0.5, borderRadius: '0.5rem', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 0.5,
                          bgcolor: compression === opt.value ? T.primaryContainer : 'transparent',
                          border: `1px solid ${compression === opt.value ? `${T.primary}30` : 'transparent'}`,
                          transition: 'all 0.15s',
                          '&:hover': { bgcolor: compression === opt.value ? T.primaryContainer : T.surfaceLow },
                        }}
                      >
                        <Icon name={opt.icon} size={14} sx={{ color: compression === opt.value ? T.primary : T.outlineVariant }} />
                        <Typography sx={{
                          fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 600,
                          color: compression === opt.value ? T.primary : T.outline,
                          display: { xs: 'none', sm: 'block' },
                        }}>
                          {opt.label}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>

                {/* Generate button */}
                <Box
                  onClick={filledCount > 0 ? handleProcess : undefined}
                  sx={{
                    px: 2.5, py: 1, borderRadius: '0.5rem', cursor: filledCount > 0 ? 'pointer' : 'default',
                    background: filledCount > 0
                      ? `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`
                      : T.surfaceHigh,
                    color: filledCount > 0 ? '#fff' : T.outlineVariant,
                    fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.82rem',
                    boxShadow: filledCount > 0 ? '0 4px 12px rgba(99,89,131,0.25)' : 'none',
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    transition: 'all 0.2s',
                    '&:hover': filledCount > 0 ? { boxShadow: '0 6px 20px rgba(99,89,131,0.35)', transform: 'translateY(-1px)' } : {},
                  }}
                >
                  <Icon name="picture_as_pdf" size={18} sx={{ color: 'inherit' }} />
                  Genereer PDF
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* ── Processing ──────────────────────────────────────────────────── */}
      {processing && (
        <Box sx={{
          bgcolor: T.surface, borderRadius: '1rem', p: { xs: 4, md: 6 },
          textAlign: 'center', boxShadow: T.shadow,
        }}>
          <IconBadge name="settings" size={64} iconSize={32} bg={T.tertiaryContainer} color={T.tertiary} />
          <Typography sx={{ fontFamily: T.font, fontSize: '1.15rem', fontWeight: 700, color: T.text, mt: 2.5, mb: 0.5 }}>
            {originalName}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, mb: 4 }}>
            PDF wordt gegenereerd met {filledCount} klikbare link{filledCount !== 1 ? 's' : ''}
          </Typography>

          <Box sx={{ maxWidth: 360, mx: 'auto', mb: 4 }}>
            {PROCESS_STEPS.map((step, i) => {
              const done = i < processStep;
              const active = i === processStep;
              return (
                <Box key={step.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '0.5rem', flexShrink: 0,
                    bgcolor: done ? T.emeraldBg : active ? T.tertiaryContainer : T.surfaceLow,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    ...(active && {
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
                    }),
                  }}>
                    <Icon
                      name={done ? 'check' : step.icon}
                      size={18}
                      sx={{ color: done ? T.emerald : active ? T.tertiary : T.outlineVariant }}
                    />
                  </Box>
                  <Typography sx={{
                    fontFamily: T.fontBody, fontSize: '0.82rem',
                    color: done ? T.text : active ? T.text : T.outlineVariant,
                    fontWeight: active ? 700 : 400,
                  }}>
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <LinearProgress
            variant="determinate"
            value={Math.min((processStep / PROCESS_STEPS.length) * 100, 95)}
            sx={{
              height: 6, borderRadius: 3, maxWidth: 360, mx: 'auto', bgcolor: T.surfaceLow,
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: `linear-gradient(135deg, ${T.tertiary}, ${T.primary})`,
              },
            }}
          />
        </Box>
      )}

      {/* ── Download ────────────────────────────────────────────────────── */}
      {downloadUrl && (
        <Box sx={{
          bgcolor: T.surface, borderRadius: '1rem', p: { xs: 4, md: 6 },
          textAlign: 'center', boxShadow: T.shadow,
        }}>
          <Box sx={{
            width: 80, height: 80, borderRadius: '1.25rem', mx: 'auto', mb: 3,
            bgcolor: T.emeraldBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="check_circle" size={44} sx={{ color: T.emerald }} />
          </Box>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.5rem', fontWeight: 800, color: T.text, mb: 0.5 }}>
            PDF klaar!
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary, mb: 4 }}>
            {outputFilename}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box
              component="a"
              href={downloadUrl}
              sx={{
                px: 3, py: 1.5, borderRadius: '0.75rem', cursor: 'pointer', textDecoration: 'none',
                background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`,
                color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.88rem',
                boxShadow: '0 4px 12px rgba(99,89,131,0.25)',
                display: 'flex', alignItems: 'center', gap: 1,
                transition: 'all 0.2s',
                '&:hover': { boxShadow: '0 6px 20px rgba(99,89,131,0.35)', transform: 'translateY(-1px)' },
              }}
            >
              <Icon name="download" size={20} sx={{ color: '#fff' }} />
              Download PDF
            </Box>
            <Box
              onClick={handleReset}
              sx={{
                px: 3, py: 1.5, borderRadius: '0.75rem', cursor: 'pointer',
                bgcolor: T.surfaceLow, color: T.text,
                fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.88rem',
                display: 'flex', alignItems: 'center', gap: 1,
                border: `1px solid ${T.outlineVariant}40`,
                transition: 'all 0.15s',
                '&:hover': { bgcolor: T.surfaceHigh },
              }}
            >
              <Icon name="restart_alt" size={20} sx={{ color: T.outline }} />
              Nieuwe presentatie
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
